import React, { useEffect, useState } from 'react'
import {
  H2,
  P,
  Button,
  Card,
  Span,
  LeftArrowIcon,
  Flex,
  Line,
  H4,
  ProgressBar,
  InputModal,
  Close,
  Input,
  InputFile,
  EditIcon,
  ToolTip,
  H5,
  SelectDropdown,
  StatusLabel,
} from '../../lib/components'
import { InvestorKYC } from './investor-kyc'
import { InvestorRiskProfile } from './investor-risk-profile'
import { useNavigate } from 'react-router-dom'
import {
  calculatePercentage,
  displayDollar,
  convertNumberWithCommas,
  formatSnakeCaseToLabel,
} from '../../lib/utils/helpers'
import {
  accountStatusMap,
  KycStatus,
  ProfileActionAllow,
  ProfileDetailsPresenterProps,
} from '../../lib/types'
import {
  tableStatusMappings,
  SOURCE_OF_FUNDS_LABELS,
  SOURCE_OF_INCOME_LABELS,
} from '../../lib/utils/message'
import { CheckCircleIcon, PendingIcon, ReportIcon, InfoIcon } from '../../lib/components/Icon'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { investorOpportunityTermsServices } from '../../services'
import { CircularProgress } from '@mui/material'
import CustomFieldEditor from './custom-field-mapping'
import {
  OPPORTUNITY_FEE_METADATA_KEYS,
  formatOpportunityFeeLabel,
  hasConfiguredFieldValue,
  validateCustomFields,
} from '../../lib/utils/fieldMapping'

const allActionsAllowed: ProfileActionAllow = {
  updateInvestmentLimit: true,
  resendVerificationLink: true,
  blockInvestor: true,
  canReadPortfolioStatements: true,
  canWritePortfolioStatements: true,
  addInvestorOpportunityTerms: true,
  canReadInvestmentLimitRequests: true,
}

const formatStatementDate = (iso: string) => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString()
}

const ProfileDetailsPresenter = ({
  investorState,
  investmentDetail,
  kycData,
  handleInvestmentLimit,
  handleSendVerificationEmailLink,
  handleBlockUnblock,
  actionAllow = allActionsAllowed,
  portfolioStatements,
  isLoadingPortfolioStatements,
  isUploadingPortfolioStatement,
  portfolioStatementsError,
  onUploadPortfolioStatement,
  onDownloadPortfolioStatement,
  onDeletePortfolioStatement,
  investorBankDetails,
}: ProfileDetailsPresenterProps) => {
  const navigate = useNavigate()
  const [displayModalInvestment, setDisplayModalInvestment] = useState(false)
  const [investmentLimitData, setInvestmentLimitData] = useState<string>('')

  // Client Opportunity Terms state
  const [showOpportunityTermsModal, setShowOpportunityTermsModal] = useState(false)
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string>('')
  const [opportunityTermsConfirmationModal, setOpportunityTermsConfirmationModal] = useState(false)
  const [opportunities, setOpportunities] = useState<any[]>([])
  const [opportunitiesData, setOpportunitiesData] = useState<any[]>([])
  const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false)
  const [opportunityTermsError, setOpportunityTermsError] = useState<string>('')
  const [existingTerms, setExistingTerms] = useState<any>(null)
  const [defaultInterestRate, setDefaultInterestRate] = useState<string>('')
  const [defaultOpportunityFees, setDefaultOpportunityFees] = useState<Record<string, string>>({})
  const [customFields, setCustomFields] = useState<Record<string, any>>({})

  const [portfolioStatementFile, setPortfolioStatementFile] = useState<File | undefined>()
  const [portfolioStatementLabel, setPortfolioStatementLabel] = useState('')

  useEffect(() => {
    setInvestmentLimitData(
      investmentDetail?.investmentLimit
        ? (Number(investmentDetail?.investmentLimit) / 100).toString()
        : '',
    )
  }, [investmentDetail])

  // Client Opportunity Terms functions
  const loadOpportunities = async () => {
    try {
      setIsLoadingOpportunities(true)
      const response =
        await investorOpportunityTermsServices.getOpportunitiesEligibleForCustomTerms()

      if (response.status === 200 && response.data) {
        const opportunitiesList = response.data.map((opp: any) => ({
          text: opp.opportunity_title,
          value: opp.id.toString(),
        }))
        setOpportunities(opportunitiesList)
        setOpportunitiesData(response.data)
      }
    } catch (error) {
      console.error('Error loading opportunities:', error)
      setOpportunityTermsError('Failed to load opportunities')
    } finally {
      setIsLoadingOpportunities(false)
    }
  }

  const checkExistingTerms = async (investorId: number, opportunityId: number) => {
    try {
      const response = await investorOpportunityTermsServices.getInvestorOpportunityTerms(
        investorId,
        opportunityId,
      )
      if (response.status === 200 && response.data) {
        setExistingTerms(response.data)
        return response.data
      } else {
        setExistingTerms(null)
        return null
      }
    } catch (error) {
      setExistingTerms(null)
      return null
    }
  }

  const handleOpportunityTermsModalOpen = async () => {
    setShowOpportunityTermsModal(true)
    // Reset state when modal opens
    setSelectedOpportunityId('')
    setDefaultInterestRate('')
    setDefaultOpportunityFees({})
    setExistingTerms(null)
    setCustomFields({})
    setOpportunityTermsError('')
    await loadOpportunities()
  }

  const handleOpportunitySelection = async (opportunityId: string) => {
    setSelectedOpportunityId(opportunityId)
    setCustomFields({})
    setOpportunityTermsError('')

    // Find and set default interest rate for selected opportunity
    const selectedOpp = opportunitiesData.find((opp) => opp.id.toString() === opportunityId)
    if (selectedOpp?.investment_return !== undefined && selectedOpp?.investment_return !== null) {
      setDefaultInterestRate(selectedOpp.investment_return.toString())
    } else {
      setDefaultInterestRate('')
    }

    const metadata = selectedOpp?.metadata ?? {}
    const feeDefaults = OPPORTUNITY_FEE_METADATA_KEYS.reduce((acc, key) => {
      const value = metadata[key]
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value)
      }
      return acc
    }, {} as Record<string, string>)
    setDefaultOpportunityFees(feeDefaults)

    // Check existing terms for selected opportunity
    if (investorState?.investor?.userId && opportunityId) {
      await checkExistingTerms(investorState.investor.userId, parseInt(opportunityId))
    }
  }

  const getCustomFieldValidationErrors = (fields: Record<string, any>) =>
    validateCustomFields(fields, formatSnakeCaseToLabel)

  const closeOpportunityTermsModals = () => {
    setOpportunityTermsConfirmationModal(false)
    setShowOpportunityTermsModal(false)
    setSelectedOpportunityId('')
    setDefaultInterestRate('')
    setDefaultOpportunityFees({})
    setCustomFields({})
    setExistingTerms(null)
    setOpportunityTermsError('')
  }

  const handleSaveOpportunityTerms = async () => {
    try {
      const errors = getCustomFieldValidationErrors(customFields)

      if (Object.keys(errors).length > 0) {
        const errorMessages = Object.values(errors).join(', ')
        setOpportunityTermsError(errorMessages)
        return
      }
      const configuredFields = Object.fromEntries(
        Object.entries(customFields).filter(([, value]) => hasConfiguredFieldValue(value)),
      )

      // Clearing all fields removes existing custom terms entirely.
      if (Object.keys(configuredFields).length === 0) {
        if (existingTerms?.id) {
          const deleteResponse =
            await investorOpportunityTermsServices.deleteInvestorOpportunityTerms(existingTerms.id)
          if (deleteResponse.status !== 200) {
            setOpportunityTermsError(
              deleteResponse.data?.message || 'Failed to clear opportunity terms',
            )
            return
          }
        }
        closeOpportunityTermsModals()
        return
      }

      const numericKeys = [
        'interest_rate',
        'portfolio_service_fee',
        'hurdle_rate',
        'carry_rate',
        'coupon_payment_interval_in_months',
        'dividend_per_unit',
      ]
      const booleanKeys = ['reinvestment_option_enabled']
      const basePayload = {
        investor_id: investorState.investor.userId,
        opportunity_id: parseInt(selectedOpportunityId, 10),
        ...configuredFields,
      }
      const sanitizedPayload: {
        [key: string]: any
        investor_id: number
        opportunity_id: number
      } = Object.fromEntries(
        Object.entries(basePayload).map(([key, value]) => {
          if (numericKeys.includes(key)) return [key, Number(value)]
          if (booleanKeys.includes(key)) return [key, String(value) === 'true']
          return [key, value]
        }),
      ) as {
        [key: string]: any
        investor_id: number
        opportunity_id: number
      }
      const response = await investorOpportunityTermsServices.createInvestorOpportunityTerms(
        sanitizedPayload,
      )

      if (response.status === 201) {
        closeOpportunityTermsModals()
      } else {
        setOpportunityTermsError(response.data?.message || 'Failed to save opportunity terms')
      }
    } catch (error: any) {
      console.error('Error saving opportunity terms:', error)
      setOpportunityTermsError(error.response?.data?.message || 'Failed to save opportunity terms')
    }
  }

  const handlePortfolioStatementUpload = async () => {
    if (!portfolioStatementFile) return
    const success = await onUploadPortfolioStatement(
      portfolioStatementFile,
      portfolioStatementLabel,
    )
    if (success) {
      setPortfolioStatementFile(undefined)
      setPortfolioStatementLabel('')
    }
  }

  return (
    <>
      {investorState && (
        <>
          <Flex>
            <Button
              classes={['auto-small-btn-width']}
              variant='secondary-btn'
              IconComponent={<LeftArrowIcon />}
              handleClick={() => navigate('/clients')}
            />
            <Span classes={['ml-16', 'font-16']} variant='bold' text={'Back To Clients'} />
          </Flex>
          <Flex variant='space-between'>
            <H2 classes={['mb-48', 'mt-16']} text='Client Profile' />
          </Flex>
          <Flex variant='row' classes={['mb-24', 'investor-profile-card-container']}>
            <Card classes={['p-40', 'mr-16', 'mb-16']}>
              <P classes={['mb-8']} text='Investment Details' />
              <Flex variant='space-between'>
                <Span variant='bold' text='Investment Limit' />
                <Span
                  variant='thin'
                  text={
                    investmentDetail?.investmentLimit || investmentDetail?.totalInvestment
                      ? `$${convertNumberWithCommas(
                          Number(investmentDetail?.totalInvestment) / 100,
                        )}/$${convertNumberWithCommas(
                          Number(investmentDetail?.investmentLimit) / 100,
                        )}`
                      : '$0/$0'
                  }
                />
              </Flex>
              <ProgressBar
                variant='default-bar'
                maxValue={
                  investmentDetail?.investmentLimit ? investmentDetail.investmentLimit : '100'
                }
                value={investmentDetail?.totalInvestment ? investmentDetail?.totalInvestment : '0'}
              />
              <Span
                variant='thin'
                text={
                  investmentDetail?.totalInvestment &&
                  investmentDetail?.investmentLimit &&
                  calculatePercentage(
                    investmentDetail?.totalInvestment,
                    investmentDetail?.investmentLimit,
                  ) > 90
                    ? 'Your investment limit is soon to be reached.'
                    : ''
                }
              />
            </Card>
            <Card classes={['p-40', 'mr-16', 'mb-16']}>
              <Flex variant='space-between'>
                <P classes={['mb-8']} text='General Details' />
                {investorState &&
                  investorState.investor?.accountStatus === accountStatusMap.DORMANT && (
                    <ToolTip content={tableStatusMappings.dormant.tooltipText}>
                      <span>
                        <PendingIcon
                          classes={['info-icon', 'ml-8']}
                          width='20px'
                          height='24px'
                          dataTestId='investor-profile-detail-dormant-user-icon'
                        />
                      </span>
                    </ToolTip>
                  )}
              </Flex>
              <Flex variant='space-between'>
                <Span variant='bold' text='Name' />
                <Span
                  variant='thin'
                  text={investorState.investor?.name ? investorState.investor.name : ''}
                />
              </Flex>
              <Flex variant='space-between'>
                <Span variant='bold' text='Email' />
                <Span
                  variant='thin'
                  text={investorState.investor?.email ? investorState.investor.email : ''}
                />
              </Flex>
              <Flex variant='space-between'>
                <Span variant='bold' text='Client ID' />
                <Span
                  variant='thin'
                  text={
                    investorState.investor?.investorReference
                      ? investorState.investor?.investorReference
                      : ''
                  }
                />
              </Flex>
              <Flex variant='space-between'>
                <Span variant='bold' text='Portfolio ID' />
                <Span
                  variant='thin'
                  text={kycData?.investor_portfolio_id ? kycData?.investor_portfolio_id : ''}
                />
              </Flex>
              <Flex variant='space-between'>
                <Span variant='bold' text='Country of residence' />
                <Span
                  variant='thin'
                  text={
                    investorState.investor?.countryOfResidence
                      ? investorState.investor.countryOfResidence
                      : ''
                  }
                />
              </Flex>
              <Flex variant='space-between'>
                <Span variant='bold' text='Mobile phone' />
                <Span
                  variant='thin'
                  text={
                    investorState.investor?.phoneNumber
                      ? investorState.investor.phoneNumber.toString()
                      : ''
                  }
                />
              </Flex>
              {investorState?.investor?.metadata?.referral_code && (
                <Flex variant='space-between'>
                  <Span variant='bold' text='Referral Code' />
                  <Span variant='thin' text={investorState.investor.metadata.referral_code} />
                </Flex>
              )}
              {investorState?.investor?.metadata?.utm_attribution && (
                <>
                  {investorState.investor.metadata.utm_attribution.utm_source && (
                    <Flex variant='space-between'>
                      <Span variant='bold' text='UTM Source' />
                      <Span
                        variant='thin'
                        text={investorState.investor.metadata.utm_attribution.utm_source}
                      />
                    </Flex>
                  )}
                  {investorState.investor.metadata.utm_attribution.utm_medium && (
                    <Flex variant='space-between'>
                      <Span variant='bold' text='UTM Medium' />
                      <Span
                        variant='thin'
                        text={investorState.investor.metadata.utm_attribution.utm_medium}
                      />
                    </Flex>
                  )}
                  {investorState.investor.metadata.utm_attribution.utm_campaign && (
                    <Flex variant='space-between'>
                      <Span variant='bold' text='UTM Campaign' />
                      <Span
                        variant='thin'
                        text={investorState.investor.metadata.utm_attribution.utm_campaign}
                      />
                    </Flex>
                  )}
                  {investorState.investor.metadata.utm_attribution.utm_content && (
                    <Flex variant='space-between'>
                      <Span variant='bold' text='UTM Content' />
                      <Span
                        variant='thin'
                        text={investorState.investor.metadata.utm_attribution.utm_content}
                      />
                    </Flex>
                  )}
                  {investorState.investor.metadata.utm_attribution.utm_term && (
                    <Flex variant='space-between'>
                      <Span variant='bold' text='UTM Term' />
                      <Span
                        variant='thin'
                        text={investorState.investor.metadata.utm_attribution.utm_term}
                      />
                    </Flex>
                  )}
                  {investorState.investor.metadata.utm_attribution.captured_at && (
                    <Flex variant='space-between'>
                      <Span variant='bold' text='UTM Captured At' />
                      <Span
                        variant='thin'
                        text={investorState.investor.metadata.utm_attribution.captured_at}
                      />
                    </Flex>
                  )}
                </>
              )}
              <Flex variant='flex-end'>
                <Button
                  classes={['mb-32', 'mt-16', 'auto-btn-width']}
                  variant={
                    investorState.investor.accountStatus === 'ACTIVE'
                      ? 'primary-btn'
                      : 'secondary-btn'
                  }
                  handleClick={() => handleBlockUnblock(investorState.investor.accountStatus)}
                  text={
                    investorState.investor?.accountStatus === 'ACTIVE'
                      ? 'Block Client'
                      : 'Unblock Client'
                  }
                  isDisabled={!actionAllow.blockInvestor}
                />
              </Flex>
            </Card>
            {investorBankDetails && (
              <Card classes={['p-40', 'mr-16', 'mb-16']}>
                <P classes={['mb-8']} text='Client Bank Details' />
                <Flex
                  variant='space-between'
                  dataTestId='investor-profile-bank-details-beneficiary-name'
                >
                  <Span variant='bold' text='Beneficiary Name' />
                  <Span variant='thin' text={investorBankDetails?.beneficiaryName ?? ''} />
                </Flex>
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investor-profile-bank-details-beneficiary-account-number'
                >
                  <Span variant='bold' text='Beneficiary Account Number' />
                  <Span variant='thin' text={investorBankDetails?.accountNumber ?? ''} />
                </Flex>
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investor-profile-bank-details-beneficiary-bank-name'
                >
                  <Span variant='bold' text={"Beneficiary's Bank Name"} />
                  <Span variant='thin' text={investorBankDetails?.beneficiaryBank ?? ''} />
                </Flex>
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investor-profile-bank-details-beneficiary-bank-address'
                >
                  <Span variant='bold' text={"Beneficiary's Bank Address"} />

                  {investorBankDetails?.bankAddress && investorBankDetails.bankAddress.length > 25 ? (
                    <ToolTip
                      content={investorBankDetails.bankAddress}
                      contentClassNames={['tool-tip-container-fit']}
                    >
                      <span>
                        <InfoIcon classes={['info-icon']} width='20px' height='24px' />
                      </span>
                    </ToolTip>
                  ) : (
                    <Span
                      variant='thin'
                      classes={['right']}
                      text={investorBankDetails?.bankAddress ?? ''}
                    />
                  )}
                </Flex>
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investor-profile-bank-details-swift-code'
                >
                  <Span variant='bold' text={'SWIFT Code'} />
                  <Span variant='thin' text={investorBankDetails?.swiftCode ?? ''} />
                </Flex>
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investor-profile-bank-details-cif'
                >
                  <Span variant='bold' text={'CIF Number'} />
                  <Span variant='thin' text={investorBankDetails?.cif ?? ''} />
                </Flex>
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investor-profile-bank-details-source-of-funds'
                >
                  <Span variant='bold' text={'Source of Funds'} />

                  {(() => {
                    const sourceOfFunds: string[] = investorBankDetails?.sourceOfFunds ?? []
                    const sourceOfFundsText = sourceOfFunds
                      .map((code: string) => SOURCE_OF_FUNDS_LABELS[code] ?? code)
                      .join(', ')

                    return sourceOfFundsText.length > 25 ? (
                      <ToolTip
                        content={sourceOfFundsText}
                        contentClassNames={['tool-tip-container-fit']}
                      >
                        <span>
                          <InfoIcon classes={['info-icon']} width='20px' height='24px' />
                        </span>
                      </ToolTip>
                    ) : (
                      <Span variant='thin' classes={['right']} text={sourceOfFundsText} />
                    )
                  })()}
                </Flex>
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investor-profile-bank-details-source-of-income'
                >
                  <Span variant='bold' text={'Source of Income'} />

                  {(() => {
                    const sourceOfIncome: string[] = investorBankDetails?.sourceOfIncome ?? []
                    const sourceOfIncomeText = sourceOfIncome
                      .map((code: string) => SOURCE_OF_INCOME_LABELS[code] ?? code)
                      .join(', ')

                    return sourceOfIncomeText.length > 25 ? (
                      <ToolTip
                        content={sourceOfIncomeText}
                        contentClassNames={['tool-tip-container-fit']}
                      >
                        <span>
                          <InfoIcon classes={['info-icon']} width='20px' height='24px' />
                        </span>
                      </ToolTip>
                    ) : (
                      <Span variant='thin' classes={['right']} text={sourceOfIncomeText} />
                    )
                  })()}
                </Flex>
              </Card>
            )}
          </Flex>

          <InvestorRiskProfile
            completed={investorState.investor?.riskProfileCompleted}
            answers={investorState.investor?.riskProfileAnswers}
          />

          {actionAllow.canReadPortfolioStatements && (
            <div className='portfolio-statements-section mb-24'>
              <Card classes={['p-40', 'mb-16']}>
                <P classes={['mb-16']} text='Portfolio Statements' />

                {actionAllow.canWritePortfolioStatements && (
                  <div className='portfolio-statements-section__upload mb-24'>
                    <InputFile
                      label='PDF file'
                      name='portfolioStatementFile'
                      type='doc'
                      file={
                        portfolioStatementFile
                          ? {
                              name: portfolioStatementFile.name,
                              type: portfolioStatementFile.type || 'application/pdf',
                              size: portfolioStatementFile.size,
                              date: new Date(),
                            }
                          : undefined
                      }
                      handleFiles={(file: any) => {
                        if (file) setPortfolioStatementFile(file as File)
                      }}
                      handleDeleteFile={() => setPortfolioStatementFile(undefined)}
                      dataTestId='profile-portfolio-statement-file'
                    />
                    <div className='portfolio-statements-section__label'>
                      <Input
                        name='portfolioStatementLabel'
                        placeholder='Label (optional, e.g. Q1 2026)'
                        type='text'
                        value={portfolioStatementLabel}
                        handleChange={(e) =>
                          setPortfolioStatementLabel(e.target.value.slice(0, 255))
                        }
                        dataTestId='profile-portfolio-statement-label'
                      />
                    </div>
                    <Button
                      classes={['auto-btn-width', 'mt-16']}
                      variant='primary-btn'
                      text={isUploadingPortfolioStatement ? 'Uploading...' : 'Upload Statement'}
                      handleClick={handlePortfolioStatementUpload}
                      isDisabled={!portfolioStatementFile || isUploadingPortfolioStatement}
                      dataTestId='profile-portfolio-statement-upload'
                    />
                  </div>
                )}

                {portfolioStatementsError && (
                  <Span
                    classes={['mb-16', 'error-text']}
                    variant='thin'
                    text={portfolioStatementsError}
                  />
                )}

                {isLoadingPortfolioStatements ? (
                  <CircularProgress size={24} />
                ) : portfolioStatements.length === 0 ? (
                  <Span variant='thin' text='No portfolio statements uploaded yet.' />
                ) : (
                  <div className='table_scroll_horizontal'>
                    <table className='styled-table'>
                      <thead>
                        <tr>
                          <th>Label</th>
                          <th>Filename</th>
                          <th>Uploaded</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {portfolioStatements.map((statement) => (
                          <tr key={statement.id}>
                            <td>{statement.label || statement.originalFilename}</td>
                            <td>{statement.originalFilename}</td>
                            <td>{formatStatementDate(statement.createdAt)}</td>
                            <td>
                              <Flex variant='row' classes={['gap-12']}>
                                <Button
                                  classes={['auto-btn-width']}
                                  variant='secondary-btn'
                                  text='Download'
                                  handleClick={() => onDownloadPortfolioStatement(statement.id)}
                                  dataTestId={`profile-portfolio-statement-download-${statement.id}`}
                                />
                                {actionAllow.canWritePortfolioStatements && (
                                  <Button
                                    classes={['auto-btn-width']}
                                    variant='secondary-btn'
                                    text='Delete'
                                    handleClick={() => onDeletePortfolioStatement(statement.id)}
                                    dataTestId={`profile-portfolio-statement-delete-${statement.id}`}
                                  />
                                )}
                              </Flex>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          )}

          <Flex classes={['upload-quarterly-report-container']}>
            <Card classes={['p-40', 'mb-16']}>
              <P classes={['mb-8']} text='Resend verification link to the Client Email' />
              <Button
                classes={['auto-btn-width']}
                variant='primary-btn'
                handleClick={handleSendVerificationEmailLink}
                text='Resend verification link'
                isDisabled={
                  !!investorState?.investor?.isEmailVerified || !actionAllow.resendVerificationLink
                }
              />
              {investorState?.investor?.isEmailVerified ? (
                <div
                  className='investment-success-sub-heading mt-16 mr-16'
                  data-testid='investor-profile-detail-verified-banner'
                >
                  <p>{'The client has verified the email.'}</p>
                </div>
              ) : (
                <div
                  className='investment-warning-sub-heading mt-16 mr-16'
                  data-testid='investor-profile-detail-not-verified-banner'
                >
                  <p>
                    {
                      'The client has not verified the email. Use this button to resend the verification link to the client email.'
                    }
                  </p>
                </div>
              )}
            </Card>
            <Card classes={['p-40', 'mb-16']}>
              <Flex variant='space-between'>
                <div>
                  <P classes={['mb-8']} text='Investment limit' />
                  {displayModalInvestment ? (
                    <InputModal>
                      <Flex
                        variant='column'
                        classes={['input-modal']}
                        dataTestId='investor-profile-detail-edit-investment-limit-modal'
                      >
                        <Flex variant='space-between'>
                          <H4 text={'Investment Limit'} />
                          <Close
                            height='24px'
                            onClick={() => setDisplayModalInvestment(false)}
                            dataTestId='investor-profile-detail-edit-investment-limit-close-button'
                          />
                        </Flex>
                        <Line />
                        <Flex variant='column' classes={['reply-section']}>
                          <Input
                            name='investmentLimit'
                            placeholder='Investment Limit'
                            type='number'
                            required
                            value={investmentLimitData || ''}
                            handleChange={(e) => setInvestmentLimitData(e.target.value)}
                            dataTestId='profile-detail-investment-limit-input'
                          />
                          <Button
                            variant='primary-btn'
                            text='Save'
                            handleClick={() => {
                              investmentLimitData &&
                                handleInvestmentLimit(Number(investmentLimitData))
                              setDisplayModalInvestment(false)
                            }}
                            dataTestId='profile-detail-investment-limit-save-button'
                          />
                        </Flex>
                      </Flex>
                    </InputModal>
                  ) : null}
                  <span data-testid='investor-profile-detail-investment-limit'>
                    {displayDollar(
                      investmentDetail?.investmentLimit
                        ? investmentDetail.investmentLimit.toString()
                        : '',
                      'USD',
                    )}
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    <EditIcon
                      classes={[
                        'align-center',
                        actionAllow.updateInvestmentLimit ? 'pointer' : 'opacity-50',
                      ]}
                      height='16px'
                      width='16px'
                      onClick={() => {
                        if (!actionAllow.updateInvestmentLimit) {
                          return
                        }
                        logCrmEvent(EngagementEvents.CLICKED_EDIT_INVESTMENT_LIMIT, {
                          investorEmail: investorState?.investor?.email,
                          investorId: investorState?.investor?.userId,
                          page: 'profile-details',
                        })

                        setDisplayModalInvestment(true)
                      }}
                      dataTestId='investor-profile-detail-edit-investment-limit-icon'
                    />
                  </span>
                  {actionAllow.canReadInvestmentLimitRequests &&
                    investorState?.investor?.userId !== null &&
                    investorState?.investor?.userId !== undefined && (
                      <div className='mt-16'>
                        <Button
                          variant='secondary-btn'
                          classes={['auto-btn-width']}
                          text='Investment limit requests'
                          handleClick={() =>
                            navigate(
                              `/investment-limit-requests?investorId=${investorState.investor.userId}`,
                            )
                          }
                          dataTestId='investor-profile-detail-investment-limit-requests-link'
                        />
                      </div>
                    )}
                  {((!investmentDetail && investorState?.investor?.kycStatus === 'APPROVED') ||
                    (investmentDetail &&
                      investmentDetail?.kycStatus === 'APPROVED' &&
                      investmentDetail?.totalInvestment &&
                      investmentDetail?.investmentLimit &&
                      calculatePercentage(
                        investmentDetail?.totalInvestment,
                        investmentDetail?.investmentLimit,
                      ) > 90) ||
                    calculatePercentage(
                      investmentDetail?.totalInvestment,
                      investmentDetail?.investmentLimit,
                    ) < 0) && (
                    <div
                      className='investment-warning-sub-heading mt-16 mr-16'
                      data-testid='investor-profile-detail-insufficient-investment-amount-disclaimer'
                    >
                      <p>
                        {
                          'The investment amount is insufficient for investing in opportunity. Admin, please assign the investment limit for the client.'
                        }
                      </p>
                    </div>
                  )}
                </div>
              </Flex>
            </Card>
          </Flex>

          <Flex variant={'row'} classes={['mt-24']}>
            <Card classes={['p-40', 'mr-16']}>
              <H5 text='Client Important checklist' classes={['mb-16']} />
              <Flex variant='space-between'>
                <Span variant='bold' text='Email Verified' />

                {investorState?.investor && investorState?.investor?.isEmailVerified ? (
                  <CheckCircleIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-email-verified-icon'
                  />
                ) : (
                  <ReportIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-email-unverified-icon'
                  />
                )}
              </Flex>

              <Flex variant='space-between' classes={['mt-4']}>
                <Span variant='bold' text='KYC Complete' />

                {kycData && kycData.status === KycStatus.APPROVED ? (
                  <CheckCircleIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-kyc-complete-icon'
                  />
                ) : (
                  <ToolTip
                    content={kycData && kycData.status}
                    contentClassNames={['tool-tip-container-fit']}
                  >
                    <span>
                      <ReportIcon
                        classes={['mr-4']}
                        height='23px'
                        dataTestId='profile-detail-kyc-incomplete-icon'
                      />
                    </span>
                  </ToolTip>
                )}
              </Flex>

              <Flex variant='space-between' classes={['mt-4']}>
                <Span variant='bold' text='Confidentiality Agreement Accepted' />

                {kycData && kycData.is_nda_accepted ? (
                  <CheckCircleIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-nda-accepted-icon'
                  />
                ) : (
                  <ReportIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-nda-not-accepted-icon'
                  />
                )}
              </Flex>

              <Flex variant='space-between' classes={['mt-4']}>
                <Span variant='bold' text='Risk Checklist Accepted' />

                {kycData && kycData.investment_essentials_agreement ? (
                  <CheckCircleIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-risk-checklist-accepted-icon'
                  />
                ) : (
                  <ReportIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-risk-checklist-not-accepted-icon'
                  />
                )}
              </Flex>

              <Flex variant='space-between' classes={['mt-4']}>
                <Span variant='bold' text='Terms of Business Accepted' />

                {kycData && kycData.accept_tob ? (
                  <CheckCircleIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-terms-of-business-accepted-icon'
                  />
                ) : (
                  <ReportIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-terms-of-business-not-accepted-icon'
                  />
                )}
              </Flex>

              <Flex variant='space-between' classes={['mt-4']}>
                <Span variant='bold' text='Transaction Checklist Accepted' />

                {kycData && kycData.transaction_checklist_agreement ? (
                  <CheckCircleIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-transaction-checklist-accepted-icon'
                  />
                ) : (
                  <ReportIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-transaction-checklist-not-accepted-icon'
                  />
                )}
              </Flex>

              <Flex variant='space-between' classes={['mt-4']}>
                <Span variant='bold' text='Client Agreement Signed' />

                {kycData && kycData.agreement_document_id ? (
                  <CheckCircleIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-agreement-signed-icon'
                  />
                ) : (
                  <ReportIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-agreement-not-signed-icon'
                  />
                )}
              </Flex>

              <Flex variant='space-between' classes={['mt-4']}>
                <Span variant='bold' text='Welcome Letter Shared' />

                {kycData && kycData?.metadata?.welcomeLetter ? (
                  <CheckCircleIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-welcome-letter-shared-icon'
                  />
                ) : (
                  <ReportIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-welcome-letter-not-shared-icon'
                  />
                )}
              </Flex>
              <Flex variant='space-between' classes={['mt-4']}>
                <Span variant='bold' text='Self Declaration Agreement' />

                {kycData && kycData?.self_declaration ? (
                  <CheckCircleIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-self-declaration-agreement-signed-icon'
                  />
                ) : (
                  <ReportIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='profile-detail-self-declaration-agreement-not-signed-icon'
                  />
                )}
              </Flex>
            </Card>

            {/* Client Opportunity Terms Section */}
            <Card classes={['p-40', 'mr-16']}>
              <P text='Client Opportunity Terms' classes={['mb-16']} />
              <Flex variant='space-between' classes={['mt-16']}>
                <Button
                  variant='primary-btn'
                  text='Add Opportunity Terms'
                  handleClick={handleOpportunityTermsModalOpen}
                  dataTestId='profile-detail-add-opportunity-terms-button'
                  isDisabled={!actionAllow.addInvestorOpportunityTerms}
                />
              </Flex>

              {/* Opportunity Terms Modal */}
              {showOpportunityTermsModal && (
                <InputModal>
                  <Flex
                    variant='column'
                    classes={['input-modal']}
                    dataTestId='profile-detail-opportunity-terms-modal'
                  >
                    <Flex variant='space-between'>
                      <H4 text='Add Client Opportunity Terms' />
                      <Close
                        height='24px'
                        onClick={() => {
                          setShowOpportunityTermsModal(false)
                          setSelectedOpportunityId('')
                          setDefaultInterestRate('')
                          setDefaultOpportunityFees({})
                          setCustomFields({})
                          setOpportunityTermsError('')
                        }}
                        dataTestId='profile-detail-opportunity-terms-modal-close-button'
                      />
                    </Flex>
                    <Line />
                    <Flex variant='column' classes={['reply-section']}>
                      <Span variant='bold' text='Select Opportunity' />
                      {isLoadingOpportunities ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SelectDropdown
                          name='opportunitySelect'
                          id='opportunitySelectId'
                          formId=''
                          list={opportunities}
                          value={selectedOpportunityId}
                          handleChange={handleOpportunitySelection}
                          dataTestId='profile-detail-opportunity-select'
                          placeholder='Select an opportunity'
                        />
                      )}

                      {selectedOpportunityId && defaultInterestRate !== '' && (
                        <div className='mt-24'>
                          <Span
                            variant='bold'
                            text={`Default Interest Rate: ${defaultInterestRate}%`}
                          />
                        </div>
                      )}

                      {selectedOpportunityId && (
                        <div className='mt-24'>
                          <Span
                            variant='bold'
                            text='Reinvestment'
                            dataTestId='profile-detail-reinvestment-guidance'
                          />
                          <p className='text-sm text-gray-600 mt-8'>
                            Reinvestment follows the client term (Reinvestment Option) when set, and
                            otherwise the client&apos;s interest preference. You can also select or
                            deselect reinvestment per client during interest payout.
                          </p>
                        </div>
                      )}

                      {selectedOpportunityId &&
                        Object.entries(defaultOpportunityFees).map(([key, value]) => (
                          <div key={key} className='mt-16'>
                            <Span
                              variant='bold'
                              text={`Default ${formatOpportunityFeeLabel(key)}: ${value}%`}
                            />
                          </div>
                        ))}

                      {selectedOpportunityId && (
                        <CustomFieldEditor
                          existingTerms={existingTerms}
                          onChange={setCustomFields}
                        />
                      )}

                      {opportunityTermsError && (
                        <div className='mt-4'>
                          <StatusLabel
                            type='error'
                            variant='basic'
                            text={opportunityTermsError}
                            dataTestId='profile-detail-opportunity-terms-error'
                          />
                        </div>
                      )}

                      {/* Confirmation Modal */}
                      {opportunityTermsConfirmationModal && (
                        <InputModal>
                          <Flex
                            variant='column'
                            classes={['input-modal']}
                            dataTestId='profile-detail-opportunity-terms-confirmation-modal'
                          >
                            <H4 text='Confirm Opportunity Terms' />
                            <Line />
                            <Flex variant='column' classes={['reply-section']}>
                              <Span
                                variant='bold'
                                text={
                                  Object.values(customFields).some(hasConfiguredFieldValue)
                                    ? 'Are you sure you want to save these opportunity terms? This action cannot be undone.'
                                    : 'Are you sure you want to clear all custom opportunity terms for this client? This action cannot be undone.'
                                }
                              />
                              <div className='mt-12'>
                                <Span variant='bold' text='Opportunity: ' />
                                <Span
                                  variant='thin'
                                  text={
                                    opportunities.find((opp) => opp.value === selectedOpportunityId)
                                      ?.text || ''
                                  }
                                />
                              </div>
                              {Object.entries(customFields)
                                .filter(([, value]) => hasConfiguredFieldValue(value))
                                .map(([key, value]) => (
                                  <div key={key} className='mt-4'>
                                    <Span
                                      variant='bold'
                                      text={`${formatSnakeCaseToLabel(key)}: `}
                                    />
                                    <Span variant='thin' text={`${value ?? '-'}`} />
                                  </div>
                                ))}

                              <Button
                                classes={['mt-24']}
                                variant='primary-btn'
                                text='Yes, Save Terms'
                                handleClick={handleSaveOpportunityTerms}
                                dataTestId='profile-detail-confirm-save-opportunity-terms'
                              />

                              <Button
                                classes={['mt-16']}
                                variant='secondary-btn'
                                text='Cancel'
                                handleClick={() => setOpportunityTermsConfirmationModal(false)}
                                dataTestId='profile-detail-cancel-save-opportunity-terms'
                              />
                            </Flex>
                          </Flex>
                        </InputModal>
                      )}

                      {selectedOpportunityId && (
                        <Button
                          classes={['mt-24']}
                          variant='primary-btn'
                          text='Save Terms'
                          handleClick={() => {
                            if (
                              Object.keys(getCustomFieldValidationErrors(customFields)).length === 0
                            ) {
                              setOpportunityTermsConfirmationModal(true)
                            } else {
                              const errors = getCustomFieldValidationErrors(customFields)
                              const errorMessages = Object.values(errors).join(', ')
                              setOpportunityTermsError(errorMessages)
                              setTimeout(() => {
                                setOpportunityTermsError('')
                              }, 1000)
                            }
                          }}
                          dataTestId='profile-detail-save-opportunity-terms-button'
                          isDisabled={!!opportunityTermsError}
                        />
                      )}
                    </Flex>
                  </Flex>
                </InputModal>
              )}
            </Card>
          </Flex>

          <Line />

          <Flex variant='space-between'>
            <H4 classes={['mb-32']} text='Client KYC' />
          </Flex>

          <Flex variant='column'>
            <InvestorKYC
              kycData={kycData}
              investorState={investorState}
              investmentDetail={investmentDetail}
            />
          </Flex>
        </>
      )}
    </>
  )
}

export { ProfileDetailsPresenter }
